import { cn } from '@commenda/utils';

export type FilterSegmentIntent = 'neutral' | 'success' | 'warning' | 'error';

export interface FilterSegment {
  id: string;
  label: string;
  count: number;
  intent?: FilterSegmentIntent;
}

export interface FilterSegmentsProps {
  segments: FilterSegment[];
  activeSegmentId: string;
  onSegmentChange: (segmentId: string) => void;
  ariaLabel?: string;
  className?: string;
}

const SEGMENT_LABEL_CLASS_NAMES: Record<FilterSegmentIntent, string> = {
  neutral: 'text-text-tertiary',
  success: 'text-text-success',
  warning: 'text-text-warning',
  error: 'text-text-error',
};

const FilterSegments = ({
  segments,
  activeSegmentId,
  onSegmentChange,
  ariaLabel,
  className,
}: FilterSegmentsProps) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={cn('grid grid-flow-col auto-cols-fr gap-lg', className)}
  >
    {segments.map((segment) => {
      const isActive = segment.id === activeSegmentId;

      return (
        <button
          key={segment.id}
          type="button"
          aria-pressed={isActive}
          onClick={() => onSegmentChange(segment.id)}
          className={cn(
            'flex items-center justify-between gap-lg rounded-md border bg-bg-primary px-xl py-2xl text-left',
            isActive
              ? 'border-fg-brand ring-4 ring-[var(--color-bg-brand-hover)]'
              : 'border-border-primary'
          )}
        >
          <span
            className={cn(
              'txt-small-medium',
              SEGMENT_LABEL_CLASS_NAMES[segment.intent ?? 'neutral']
            )}
          >
            {segment.label}
          </span>
          <span className="txt-large-medium text-text-primary">
            {segment.count}
          </span>
        </button>
      );
    })}
  </div>
);

FilterSegments.displayName = 'FilterSegments';

export { FilterSegments };
